import { WithOwner } from "../IDBSchema";
import { EntityDataApi } from "../IndexedDBApi";
import { ConstructionPlanBase, ConstructionPlanBasePatchPayload, ConstructionPlanBasePutPayload } from "../../../schema/generated/ConstructionPlan";
import { getFromIdPayload, getFromIntIdPayload, setDeletePayload } from "../../../schema/general";
import { constructionPlanRow, constructionPlanRows } from "../../../schema/console/construction_plan";
import { PutStatusResponse, StatusResponse } from "../../../schema/console/statusData";
import { WorkBase } from "../../../schema/generated/Work";
import { idbEntityDefs } from "../generated/entityDefs";
import { required } from "../../../util/typed";

export class ConstructionPlanDataApi extends EntityDataApi<ConstructionPlanBase> {
  public readonly def = idbEntityDefs.ConstructionPlan;

  public async getAll(ownerId: string): Promise<ConstructionPlanBase[]> {
    return await this.listByOwner(ownerId);
  }

  public async getById(payload: WithOwner<getFromIdPayload>): Promise<ConstructionPlanBase | null> {
    return await this.findById(payload);
  }

  // 工事(Work, id=number)を id 索引した Map を作る。工事は生成された薄い DataApi(Works ストア)に
  // キャッシュされる。施工計画の工事解決は alive のみ(オンラインの include where deleted_at:null と同方針)。
  private async aliveWorkMap(ownerId: string): Promise<Map<number, WorkBase>> {
    const works = await this.IDB().generated.work.getAllByOwner(ownerId);
    return new Map(works.map((work) => [work.id, work]));
  }

  public async getRowById(payload: WithOwner<getFromIdPayload>): Promise<constructionPlanRow | null> {
    const data = await this.getById(payload);
    if (!data) {
      return null;
    }
    const user = await this.IDB().userDataApi.getFullDataById({ id: data.preparer_user_id, ownerId: payload.ownerId });
    const works = await this.aliveWorkMap(payload.ownerId);
    const work = data.work_id != null ? works.get(data.work_id) ?? null : null;
    return { ...data, Work: work, User: user };
  }

  public async getRowsByWorkId(payload: WithOwner<getFromIntIdPayload>): Promise<constructionPlanRows> {
    const constructionPlans = (await this.getAll(payload.ownerId)).filter((plan) => plan.work_id === payload.id);
    const works = await this.aliveWorkMap(payload.ownerId);
    const userIds = [...new Set(constructionPlans.map((plan) => plan.preparer_user_id))];
    const users = await Promise.all(
      userIds.map((id) =>
        this.IDB().userDataApi.getFullDataById({ id, ownerId: payload.ownerId })
      )
    );
    const userMap = new Map(users.map((user) => [user.id, user]));
    return constructionPlans.map((plan) => ({
      ...plan,
      Work: plan.work_id != null ? works.get(plan.work_id) ?? null : null,
      User: required(userMap.get(plan.preparer_user_id), "登録者ユーザー"),
    }));
  }

  public async put(data: WithOwner<ConstructionPlanBasePutPayload>): Promise<PutStatusResponse> {
    return await this.createRow(data);
  }

  public async patch(data: WithOwner<ConstructionPlanBasePatchPayload>): Promise<StatusResponse> {
    return await this.patchRow(data);
  }

  public async delete(data: WithOwner<setDeletePayload>): Promise<StatusResponse> {
    return await this.softDelete(data);
  }
}
